import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'How to add drivers and team members | Paddiq';

export function AddTeamMembersPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Team guide</p>
            <h1>Add drivers and team members.</h1>
            <p className="guide-hero__lead">
              Choose the right account type, set up access, and handle guardian details for minor
              drivers in your Paddiq workspace.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/drivers')}
              >
                Open Team
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/getting-started"
              >
                View getting started
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 15, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/add-team-members" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#prepare">Choose the account</a>
                </li>
                <li>
                  <a href="#create">Create the account</a>
                </li>
                <li>
                  <a href="#minors">Set up a minor driver</a>
                </li>
                <li>
                  <a href="#access">Share account access</a>
                </li>
                <li>
                  <a href="#requests">Review join requests</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you start</strong>
                <span>
                  Confirm the person's name, role, and—for a driver—their date of birth. A parent
                  creating a child login chooses a username; other account flows use an email.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="prepare">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Account type</p>
                  <h2>Decide what the person needs to do.</h2>
                  <p>
                    Paddiq offers driver, coach, mechanic, and parent or guardian account types in a
                    club team workspace. Family workspaces can add drivers and parents or guardians,
                    but not coaches or mechanics.
                  </p>
                  <p>
                    A driver account also creates the driver's roster profile. Use a coach,
                    mechanic, or parent account when the person needs that role without creating a
                    new driver profile.
                  </p>
                  <div className="guide-callout">
                    Account and driver controls are role-aware. If you cannot see the action in this
                    guide, ask a workspace owner to complete it.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="create">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Team</p>
                  <h2>Create the account from Team.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Team</strong> in the main navigation.
                    </li>
                    <li>
                      Select <strong>Create Account</strong> and choose the account type.
                    </li>
                    <li>Enter the person's full name.</li>
                    <li>
                      If you are signed in as a parent creating your child's driver account, choose
                      an available <strong>Username</strong> and a <strong>Child password</strong>{' '}
                      of at least eight characters. You can use <strong>Generate</strong> for the
                      password. This flow does not ask for a child email or setup link.
                    </li>
                    <li>
                      For other account creation flows, enter the person's email and choose{' '}
                      <strong>Set password now</strong> or <strong>Create setup link</strong>.
                    </li>
                    <li>
                      For a driver, add the required date of birth and any optional driver or
                      license number.
                    </li>
                    <li>Review any access or billing notice, then create the account.</li>
                  </ol>
                  <p>
                    Paddiq may require confirmation when the new account changes workspace usage.
                    Review the notice shown in the product before continuing.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="minors">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Drivers under 18</p>
                  <h2>Choose guardian-linked or team-managed access.</h2>
                  <p>
                    When you create your child while signed in as a parent, Paddiq automatically
                    links the minor driver to your parent account. Check the linked-parent notice;
                    you do not need to create your own parent account again.
                  </p>
                  <p>
                    In other account creation flows, a driver under 18 needs a parent or guardian
                    account with a separate login email unless eligible team-managed access is
                    selected. You can also add a second parent or guardian while creating the
                    driver.
                  </p>
                  <p>
                    Team owners and coaches can instead mark a minor as a{' '}
                    <strong>Team-managed driver</strong>. That keeps the driver under direct team
                    management without creating a parent account. Choose this only when it matches
                    how your team manages that driver.
                  </p>
                  <div className="guide-ready">
                    <h3>Before creating a minor driver, confirm:</h3>
                    <ul>
                      <li>the driver's date of birth is correct;</li>
                      <li>
                        your existing parent link or the new guardian details are correct; and
                      </li>
                      <li>the driver should be guardian-linked or team-managed.</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="guide-step" id="access">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Sign-in setup</p>
                  <h2>Give the new member their access.</h2>
                  <p>
                    For a child login you created as a parent, use the child's username and child
                    password to sign in. Share those details through your family's secure channel.
                    The child does not need a setup link.
                  </p>
                  <p>
                    For other accounts, if you set a password, share the sign-in details through
                    your team's approved secure channel. If you chose a setup link, Paddiq shows a
                    registration link and its expiration date after the account is created.
                  </p>
                  <ol className="guide-instructions">
                    <li>Copy each setup link shown in the success message.</li>
                    <li>Send it to the matching email recipient before it expires.</li>
                    <li>Ask the member to finish registration and sign in.</li>
                    <li>Confirm the person appears in the intended Team roster group.</li>
                  </ol>
                  <div className="guide-callout">
                    An email-based minor-driver setup can produce more than one link when parent or
                    guardian accounts are created at the same time. Match every link to the email
                    shown next to it.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="requests">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Team code</p>
                  <h2>Review requests from existing accounts.</h2>
                  <p>
                    A driver or parent with an existing Paddiq account can request access using your
                    team code instead of having you create another account. The request stays
                    pending until a team administrator reviews it.
                  </p>
                  <ol className="guide-instructions">
                    <li>Find your team code in the Team overview.</li>
                    <li>Share the code with the driver or parent who needs to request access.</li>
                    <li>Open the Invitations list when their join request arrives.</li>
                    <li>Check the person's name, email, role, and any requested drivers.</li>
                    <li>
                      Select <strong>Accept</strong> only when the request is correct.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If account setup is blocked</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Create Account is missing</h3>
                    <p>
                      Your role may not manage team accounts, or the workspace may require an access
                      step. Ask a workspace owner to review the account and workspace state.
                    </p>
                  </article>
                  <article>
                    <h3>A role is not available</h3>
                    <p>
                      Family workspaces support driver and parent or guardian accounts. Coach and
                      mechanic accounts are available in club team workspaces.
                    </p>
                  </article>
                  <article>
                    <h3>A minor cannot be created</h3>
                    <p>
                      Confirm the date of birth. If you are creating your child as a parent, check
                      the username, child password, and linked-parent notice. Otherwise, add the
                      required guardian details or have an eligible owner or coach choose
                      Team-managed driver.
                    </p>
                  </article>
                  <article>
                    <h3>A setup link has expired</h3>
                    <p>
                      Do not create a duplicate account. Contact the workspace owner or Paddiq
                      support for help restoring the intended account's setup access.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Ready to build the roster?</p>
                  <h2>Add the right people to Team.</h2>
                  <p>
                    Choose each role carefully, then send any setup links to the matching person.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/drivers')}
                  >
                    Open Team
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
